import _ from "lodash";
import React, {
  forwardRef,
  useCallback,
  useImperativeHandle,
  useState,
  useEffect,
} from "react";
import cx from "classnames";

// HOCs
import withRecorder from "../WithRecorder";
import I18nHOC from "UIComponents/I18nHOC";
import MediaPlaceholder from "UIComponents/MediaPlaceholder";

// apollo client methods
import { compose } from "lib/apolloClient";

// redux methods
import { connect } from "react-redux";
import { setToastMsg } from "Login/modules/LoginModule/LoginActions";
import { downloadFromUrl, updateAudioRecorderBlob } from "modules/Services";
import { Alert, Button, Dialog, IconButton, Modal } from "@toddle-edu/ds-web";
import {
  CloseOutlined,
  SquareFilled,
  PauseFilled,
  ReloadArrowOutlined,
  ScreenRecordOutlined,
  TickLargeOutlined,
  MicrophoneFilled,
} from "@toddle-edu/ds-icons";

// styles
import classes from "./AudioRecorderV4.scss";
import { prepareAttachmentFromBlob } from "Utils";
import { createPortal } from "react-dom";

const AudioRecorderV4 = forwardRef((props, forwardedRef) => {
  const {
    t,
    draftRecordingBlob,
    formattedLimit,
    isPaused,
    isPermissionGranted,
    hadDraftRecording,
    handleVisualiserRefUpdate,
    pauseStateBlob,
    showLimit = true,
    timer,
    timeStamp,
    title = t("common:audio"),
    cancelRecording,
    closeModal,
    restartRecording,
    startRecording,
    stopRecording,
    togglePause,
    reloadText = t("common:start_over_caps"),
    submitText,
    showRestartDialogV2 = false,
    showStartOverDialogV2 = false,
    permissionWarningAlertStyle = {},
    testId = "audioRecorder",
  } = props;

  // hooks
  const [alertVisiblity, setAlertVisibility] = useState(false);
  const [isPreRecordingState, setIsPreRecordingState] = useState(false);
  const [preRecordingTimer, setPreRecordingTimer] = useState({
    timeoutId: null,
    time: 3,
    intervalId: null,
    showTimer: true,
  });
  const [isRecording, setIsRecording] = useState(false);
  const [isRecordingComplete, setIsRecordingComplete] = useState(false);
  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const [isStartOverDialogOpen, setIsStartOverDialogOpen] = useState(false);

  useEffect(() => {
    if (hadDraftRecording) {
      setIsRecording(true);
    }
    return () => {
      if (preRecordingTimer?.timeoutId) {
        clearTimeout(preRecordingTimer.timeoutId);
        clearInterval(preRecordingTimer.intervalId);
      }
      cancelRecording();
    };
  }, []);

  useImperativeHandle(forwardedRef, () => ({
    cancelRecording: () => {
      cancelRecording();
    },
  }));

  const onVisualiserContainerRefUpdate = useCallback(node => {
    if (node !== null) {
      handleVisualiserRefUpdate(node);
    }
  }, []);

  // Classes and styles
  const recordingDetailsContainerClasses = cx(
    classes.recordingDetails,
    "heading-6",
    isRecording && classes.isRecording
  );

  const recordingIndicatorClasses = cx(
    classes.recordingIndicator,
    isRecording && classes.active
  );

  // attachment / pause state blob prepration
  const attachment = () => {
    if (pauseStateBlob) {
      return prepareAttachmentFromBlob(pauseStateBlob);
    } else if (draftRecordingBlob) {
      return prepareAttachmentFromBlob(draftRecordingBlob);
    }
    return null;
  };

  // methods
  const onAlertClose = () => {
    cancelRecording();
    setAlertVisibility(false);
  };

  const cancelRecordingAndCloseModal = async () => {
    await cancelRecording();
    closeModal();
  };

  const closeModalIfConditionMet = () => {
    if (timeStamp > 0) {
      setIsDialogOpen(true);
    } else {
      cancelRecordingAndCloseModal();
    }
  };

  const closeDialogAndModal = () => {
    setIsDialogOpen(false);
    cancelRecordingAndCloseModal();
  };

  const startCountdownAndRecording = () => {
    if (!isPermissionGranted) {
      setAlertVisibility(true);
    } else {
      setIsPreRecordingState(true);
      const timerId = setTimeout(() => {
        setIsPreRecordingState(false);
        setIsRecording(true);
        setIsRecordingComplete(false);
        startRecording();
        clearInterval(preRecordingTimer.intervalId);
      }, 3000);
      const intervalId = setInterval(() => {
        setPreRecordingTimer(prevState => {
          if (prevState?.time < 0) {
            clearInterval(intervalId);
          }
          return {
            ...prevState,
            time: prevState.time - 1,
          };
        });
      }, 1000);
      setPreRecordingTimer({
        ...preRecordingTimer,
        timeoutId: timerId,
        intervalId: intervalId,
        showTimer: true,
      });
    }
  };

  const stopCountdownAndRecording = () => {
    setIsPreRecordingState(false);
    setIsRecording(false);
    setIsRecordingComplete(false);
    clearTimeout(preRecordingTimer.timeoutId);
    clearInterval(preRecordingTimer.intervalId);
    setPreRecordingTimer({ timeoutId: null, time: 3 });
  };

  const pauseRecordingAndShowOptions = () => {
    togglePause(true);
    setIsRecording(false);
    setIsRecordingComplete(true);
  };

  const stopRecordingAndStartOver = async () => {
    await cancelRecording();
    await restartRecording();
  };

  const renderModal = () => {
    return (
      <div className={classes.modalContainerBackdrop}>
        {!isPermissionGranted && (
          <div
            className={classes.alertContainer}
            style={permissionWarningAlertStyle}
          >
            {alertVisiblity ? (
              <Alert
                title={t("common:microphone_permission_message")}
                type={"warning"}
                isClosable={true}
                onClose={onAlertClose}
                testId={`${testId}-micPermission`}
              />
            ) : null}
          </div>
        )}
        <div className={classes.modal}>
          {/* Header */}
          <div className={classes.modalHeader}>
            {/* Modal title */}
            <div className={`${classes.modalTitle} heading-4`}>{title}</div>
            {/* Close button */}
            <IconButton
              size="medium"
              _legacy_variant="plain"
              type="plain"
              variant="neutral"
              icon={<CloseOutlined />}
              onClick={closeModalIfConditionMet}
              testId={`${testId}-closeRecordingModal`}
            />
          </div>
          {/* Body */}
          <div className={classes.modalBody}>
            <div className={classes.visualsContainer}>
              <div
                className={classes.visualiserContainer}
                ref={onVisualiserContainerRefUpdate}
              ></div>
              <div className={recordingDetailsContainerClasses}>
                <div className={recordingIndicatorClasses} />
                <span>{timer}</span>
                {showLimit ? <span>/{formattedLimit}</span> : null}
              </div>
              {isPaused && (
                <MediaPlaceholder
                  attachments={[attachment()]}
                  containerStyle={{
                    bottom: 0,
                    left: 0,
                    position: "absolute",
                    right: 0,
                    top: 0,
                  }}
                  showAudioDownloadButton={true}
                  showWaveform={true}
                  testId={testId}
                />
              )}
            </div>
          </div>
          <div className={classes.modalFooter}>
            {/* If recording has begun and permissions have been granted
          then show the pause/resume and sto buttons
          else show start button or post recording buttons based on isRecordingComplete */}
            {isRecording && isPermissionGranted ? (
              <div className={classes.recordingButtonsCotnainer}>
                <Button
                  isFullWidth={true}
                  size="large"
                  variant="progressive"
                  icon={isPaused ? <MicrophoneFilled /> : <PauseFilled />}
                  onClick={() => togglePause(!isPaused)}
                  testId={`${testId}-toggleResumePause`}
                >
                  {isPaused ? t("common:resume") : t("common:pause")}
                </Button>
                <Button
                  isFullWidth={true}
                  size="large"
                  icon={<SquareFilled />}
                  onClick={pauseRecordingAndShowOptions}
                  testId={`${testId}-stopRecording`}
                >
                  {t("common:stop")}
                </Button>
              </div>
            ) : isRecordingComplete ? (
              <div className={classes.recordingButtonsCotnainer}>
                <Button
                  size="large"
                  _legacy_variant="inline"
                  variant="neutral"
                  type="inline"
                  icon={<ReloadArrowOutlined />}
                  onClick={() => setIsStartOverDialogOpen(true)}
                  testId={`${testId}-restartRecording`}
                >
                  {reloadText}
                </Button>
                <Button
                  size="large"
                  variant="progressive"
                  icon={<TickLargeOutlined />}
                  onClick={stopRecording}
                  testId={`${testId}-saveRecording`}
                >
                  {submitText || t("common:done")}
                </Button>
              </div>
            ) : isPreRecordingState ? (
              <Button
                isFullWidth={true}
                isActivated={!preRecordingTimer.showTimer}
                size="large"
                onClick={stopCountdownAndRecording}
                onMouseEnter={() =>
                  setPreRecordingTimer({
                    ...preRecordingTimer,
                    showTimer: false,
                  })
                }
                onMouseLeave={() =>
                  setPreRecordingTimer({
                    ...preRecordingTimer,
                    showTimer: true,
                  })
                }
                testId={`${testId}-cancelRecording`}
              >
                {preRecordingTimer.showTimer
                  ? // ? add localisation for this line
                    t("common:recording_begins_in_time", {
                      time: preRecordingTimer.time,
                    })
                  : t("common:cancel_recording")}
              </Button>
            ) : (
              <Button
                isFullWidth={true}
                size="large"
                icon={<ScreenRecordOutlined />}
                onClick={startCountdownAndRecording}
                testId={`${testId}-startRecording`}
              >
                {t("workbook:start_recording")}
              </Button>
            )}
          </div>
        </div>
      </div>
    );
  };

  return (
    <>
      {isDialogOpen ? (
        showRestartDialogV2 ? (
          <Modal
            width={"480px"}
            height={"280px"}
            hasOverlay={true}
            shouldCloseOnOverlayClick={true}
            shouldCloseOnEsc={true}
            onClose={() => {
              setIsDialogOpen(false);
            }}
          >
            <Modal.Header
              title={t("common:discard_label", {
                label: t("common:recording"),
              })}
            />
            <Modal.Body>{t("journal:recorded_audio_lost")}</Modal.Body>
            <Modal.Footer>
              <div className={classes.reloadAgainModalFooter}>
                <Button
                  onClick={() => {
                    setIsDialogOpen(false);
                  }}
                  size="large"
                  variant="outlined-subtle"
                  testId={`${testId}-restartRecordingModal-cancel`}
                >
                  {t("common:cancel")}
                </Button>
                <Button
                  onClick={closeDialogAndModal}
                  size="large"
                  testId={`${testId}-restartRecordingModal-discard`}
                >
                  {t("common:discard_changes")}
                </Button>
              </div>
            </Modal.Footer>
          </Modal>
        ) : (
          <Dialog
            buttonOneText={t("common:cancel")}
            buttonTwoText={t("workbook:discard_with_label", {
              label: `${t("common:and_label")} ${t("common:close")}`,
            })}
            message={t("common:recorded_audio_will_be_lost")}
            shouldCloseOnEsc={false}
            shouldCloseOnOverlayClick={false}
            title={t("common:discard_label", {
              label: t("common:recording"),
            })}
            testId={`${testId}-discardRecording`}
            onButtonOneClick={() => setIsDialogOpen(false)}
            onButtonTwoClick={closeDialogAndModal}
            onClose={() => setIsDialogOpen(false)}
          />
        )
      ) : null}
      {isStartOverDialogOpen ? (
        showStartOverDialogV2 ? (
          <Modal
            width={"480px"}
            height={"280px"}
            hasOverlay={true}
            shouldCloseOnOverlayClick={true}
            shouldCloseOnEsc={true}
            onClose={() => {
              setIsStartOverDialogOpen(false);
            }}
          >
            <Modal.Header
              title={t("common:discard_label", {
                label: t("common:recording"),
              })}
            />
            <Modal.Body>{t("journal:recording_progress_lost")}</Modal.Body>
            <Modal.Footer>
              <div className={classes.reloadAgainModalFooter}>
                <Button
                  onClick={() => {
                    setIsStartOverDialogOpen(false);
                  }}
                  size="large"
                  variant="outlined-subtle"
                  testId={`${testId}-restartRecordingModal-cancel`}
                >
                  {t("common:cancel")}
                </Button>
                <Button
                  testId={`${testId}-restartRecordingModal-discard`}
                  onClick={stopRecordingAndStartOver}
                  size="large"
                >
                  {t("workbook:discard_with_label", {
                    label: `${t("common:and_label")} ${t("common:start_over")}`,
                  })}
                </Button>
              </div>
            </Modal.Footer>
          </Modal>
        ) : (
          <Dialog
            buttonOneText={t("common:cancel")}
            buttonTwoText={t("workbook:discard_with_label", {
              label: `${t("common:and_label")} ${t("common:start_over")}`,
            })}
            // ? add localisation for this line
            message={t("common:recording_in_progress_will_be_lost")}
            shouldCloseOnEsc={false}
            shouldCloseOnOverlayClick={false}
            // ? add localisation for this line
            title={t("common:start_recording_again")}
            onButtonOneClick={() => setIsStartOverDialogOpen(false)}
            onButtonTwoClick={stopRecordingAndStartOver}
            onClose={() => setIsStartOverDialogOpen(false)}
            testId={`${testId}-startOver`}
          />
        )
      ) : null}
      {/*
        ! Using react portal here instead of UIModal or DS-Modal because
          These two modals are obstructing the audio recording "post-draft state"
          i.e. if you have something drafted and you open the modal, the modal
          gives you two options to either discard the draft or continue recording
          but the modal is obstructing the normal flow of these two options

          1. If you continue recording the previous draft is discarded and wont't
            be merged with the new recording.

          2. If you discard recording, the draft is discarded but the recorder doesn't
            pauses the timer while the recording is actually paused. So the actual recording
            is smaller than the timer.

          Using react portal solved these issues and the features are working as expected.
       */}
      {createPortal(renderModal(), document.body)}
    </>
  );
});

const mapActionCreators = {
  setToastMsg,
  updateAudioRecorderBlob,
  downloadFromUrl,
};
const mapStateToProps = state => {
  return {
    draftRecording: _.get(state, "app_services.draftRecording", null),
  };
};

const ComposedAudioRecorderV4 = compose(
  I18nHOC({ resource: ["common", "workbook"] }),
  connect(mapStateToProps, mapActionCreators, null, { forwardRef: true }),
  withRecorder
)(AudioRecorderV4);

const defaultProps = {
  autoStartRecording: false,
  limit: 3540000,
  saveDraftRecording: true,
};

AudioRecorderV4.displayName = "AudioRecorderV4";
AudioRecorderV4.defaultProps = defaultProps;

ComposedAudioRecorderV4.defaultProps = defaultProps;

export default ComposedAudioRecorderV4;
